// src/components/leitner/TaskCreatorManager.jsx
// Two-column layout with expandable questions, all question types, marks calculation

import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Save, ChevronUp, ChevronDown, X, Plus, Image, Loader2, Upload,
  Globe, Users, Lock, Calendar, Trophy, Timer, Shuffle, ListOrdered, Layers,
  Eye, Mail, Award, ChevronRight, FileText, Columns, FileDown, BookOpen, Link, Sparkles, Wand2,
  HelpCircle, BrainCircuit
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { taskService } from '../../services/taskService';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import CategoryFilter from './CategoryFilter';
import { MathRenderer } from '../math/MathRenderer';
import InlineMathField from '../math/InlineMathField';
import PdfExportModal from './PdfExportModal';

const USERS_COLLECTION = 'pb-users';
const GUIDED_PROMPT_PRESETS = [
  {
    key: 'math_g11',
    label: 'Mathematics Grade 11',
    subject: 'Mathematics',
    grade: '11',
    prompt: `Use Guided Reconstruction Practice for CAPS Mathematics Grade 11.
Follow this sequence: classify -> strategise -> reconstruct -> debate -> compute -> reflect.
Create 5-6 progressive steps with:
- structure recognition before computation
- one first-move strategy prompt
- worked example with focused reconstruction gaps
- classroom debate misconception checkpoint
- one practice item with model answer and hints
- meta-reflection on prior knowledge transfer.
Use language suitable for Grade 11 learners in South Africa and preserve mathematical notation with LaTeX where relevant.`,
    referenceExample: `Reference style example (Grade 11):
Topic: simultaneous equations (linear + quadratic).
Flow: identify line+curve system -> predict max intersections -> isolate from linear equation -> substitute into quadratic -> expand binomial with middle term -> solve quadratic -> substitute back -> reflect on reused prior knowledge.`
  },
  {
    key: 'math_g12',
    label: 'Mathematics Grade 12',
    subject: 'Mathematics',
    grade: '12',
    prompt: `Use Guided Reconstruction Practice for CAPS Mathematics Grade 12.
Follow this sequence: classify -> strategise -> reconstruct -> debate -> compute -> reflect.
Create 6 steps with explicit conceptual checkpoints, then practice.
For each step include:
- concise concept objective
- worked example explanation
- one practice question
- model answer with reasoning
- 1-2 hints
Focus on exam-ready reasoning quality and common Grade 12 misconceptions.`,
    referenceExample: `Reference style example (Grade 12):
Financial mathematics with two deposits.
Flow: classify cash-flow structure -> place deposits on timeline -> decide exponents by time distance -> compute each future value separately -> justify same-time addition -> reflect on prior knowledge (formula, exponents, timeline, value alignment).`
  }
];

const ROLE_GROUPS = {
  officials: ['official', 'district_official', 'district-official'],
  educators: ['educator', 'teacher'],
  tutors: ['tutor'],
  learners: ['learner', 'student']
};

const getDisplayName = (user) => user?.displayName || user?.email || user?.id || 'Unknown';
const getAffiliationValue = (user, key, fallback = '') =>
  user?.affiliation?.[key] || user?.[key] || fallback;

const TaskCreatorManager = ({ bookId, questions, task, onSave, onCancel, onTaskPdfUpdated }) => {
  const { user } = useAuth();
  const isEditing = !!task;
  const [viewMode, setViewMode] = useState('creator'); // 'creator' | 'processor'
  const [showPdfExport, setShowPdfExport] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });

  // Question selection
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(task?.questionIds || []);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);

  // Notes & sections
  const [availableNotes, setAvailableNotes] = useState([]);
  const [sections, setSections] = useState(() => {
    if (task?.sections?.length) return task.sections;
    if (task?.questionIds?.length) return task.questionIds.map(id => ({ type: 'question', id }));
    return [];
  });
  const [contentMode, setContentMode] = useState('questions'); // 'questions' | 'notes'
  const [showNoteLinker, setShowNoteLinker] = useState(null); // section index or null

  // User assignment
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [accessMode, setAccessMode] = useState('email'); // 'email' | 'organisation' | 'my-learners'
  const [orgType, setOrgType] = useState('project'); // 'project' | 'school' | 'district'
  const [orgName, setOrgName] = useState('');
  const [accessAutoKey, setAccessAutoKey] = useState('');
  const [allLearners, setAllLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingTaskPdf, setUploadingTaskPdf] = useState(false);
  const [uploadingGuidedPdf, setUploadingGuidedPdf] = useState(false);
  const [generatingGuided, setGeneratingGuided] = useState(false);

  // Form state
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('questions'); // 'questions', 'access', 'settings'
  const [processingPages, setProcessingPages] = useState([{ id: 'page-1', content: '' }]);
  const [activeProcessingPageId, setActiveProcessingPageId] = useState('page-1');
  const [inlineMathEnabled, setInlineMathEnabled] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingOptions, setProcessingOptions] = useState({
    generateQuiz: true,
    generateExercises: false
  });
  const [processingResults, setProcessingResults] = useState(null);

  const [formData, setFormData] = useState({
    type: task?.type || 'leitner_quiz',
    name: task?.name || '',
    description: task?.description || '',
    visibility: task?.visibility || 'private',
    requiresSignIn: task?.requiresSignIn !== false,
    assignedUsers: task?.assignedUsers || [],
    isTimeBound: task?.isTimeBound || false,
    startDate: task?.startDate ? formatDateForInput(task.startDate) : '',
    endDate: task?.endDate ? formatDateForInput(task.endDate) : '',
    passingScore: task?.passingScore || 70,
    timeLimitPerQuestion: task?.timeLimitPerQuestion || 0,
    shuffleQuestions: task?.shuffleQuestions !== false,
    showAnswersAfterEachResponse: task?.showAnswersAfterEachResponse === true,
    showAnswersAfter: task?.showAnswersAfter !== false,
    maxAttempts: task?.maxAttempts || 0,
    displayMode: task?.displayMode || 'one-at-a-time',
    coLearnerEnabled: task?.coLearnerEnabled !== false,
    coLearnerCrossTask: task?.coLearnerCrossTask !== false,
    coLearnerPrompt: task?.coLearnerPrompt || '',
    ogTitle: task?.ogTitle || '',
    ogDescription: task?.ogDescription || '',
    ogImage: task?.ogImage || '',
    taskPdfUrl: task?.taskPdfUrl || '',
    taskPdfFilename: task?.taskPdfFilename || '',
    taskPdfUploadedAt: task?.taskPdfUploadedAt || null,
    guidedReconstruction: task?.guidedReconstruction || {
      presetKey: 'math_g11',
      subject: 'Mathematics',
      grade: '11',
      sourcePdfUrl: '',
      sourcePdfFilename: '',
      sourcePrompt: '',
      topicFocus: '',
      specificProblem: '',
      title: '',
      summary: '',
      generatedAt: null,
      steps: []
    },
    creatorEmail: user?.email || ''
  });

  function formatDateForInput(date) {
    if (!date) return '';
    const d = date?.toDate?.() || new Date(date);
    return d.toISOString().slice(0, 16);
  }

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      if (bookId) {
        try {
          const cats = await leitnerService.getQuestionCategories(bookId);
          setCategories(cats);
        } catch (error) {
          console.error('Failed to load categories:', error);
        }
      }
    };
    loadCategories();
  }, [bookId]);

  // Load notes
  useEffect(() => {
    const loadNotes = async () => {
      if (!bookId) return;
      try {
        const notesData = await leitnerService.getLeitnerNotes(bookId);
        setAvailableNotes(notesData);
      } catch (error) {
        console.error('Failed to load notes:', error);
      }
    };
    loadNotes();
  }, [bookId]);

  useEffect(() => {
    const loadLearners = async () => {
      if (!bookId || activeSection !== 'access') return;
      setLoadingLearners(true);
      try {
        const usersRef = collection(db, USERS_COLLECTION);
        const q = query(usersRef, where('affiliation.role', 'in', ROLE_GROUPS.learners));
        const snapshot = await getDocs(q);
        const learnerList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllLearners(learnerList);
      } catch (error) {
        console.error('Failed to load learners:', error);
        setAllLearners([]);
      } finally {
        setLoadingLearners(false);
      }
    };
    loadLearners();
  }, [bookId, activeSection]);

  // Filter questions (NO type filtering - all questions available)
  const filteredQuestions = useMemo(() => {
    return leitnerService.filterQuestionsByCategories(questions, filters);
  }, [questions, filters]);

  // Available = filtered minus already selected
  const availableQuestions = filteredQuestions.filter(q => !selectedQuestionIds.includes(q.id));

  // Selected questions in order
  const selectedQuestions = selectedQuestionIds
    .map(id => questions.find(q => q.id === id))
    .filter(Boolean);
  const isGuidedTask = formData.type === 'guided_reconstruction';
  const selectedGuidedPreset = GUIDED_PROMPT_PRESETS.find(
    (preset) => preset.key === formData.guidedReconstruction?.presetKey
  ) || null;
  const guidedStepsCount = formData.guidedReconstruction?.steps?.length || 0;
  const hasStandardContent = sections.length > 0 || selectedQuestionIds.length > 0;
  const canSaveTask = Boolean(
    formData.name?.trim() &&
    (
      isGuidedTask
        ? (
          formData.guidedReconstruction?.sourcePdfUrl &&
          formData.guidedReconstruction?.sourcePrompt?.trim() &&
          guidedStepsCount > 0
        )
        : hasStandardContent
    )
  );

  useEffect(() => {
    if (!isGuidedTask) return;

    const existing = formData.guidedReconstruction || {};
    const preset =
      GUIDED_PROMPT_PRESETS.find((item) => item.key === existing.presetKey) ||
      GUIDED_PROMPT_PRESETS[0];

    const needsBackfill = !existing.presetKey || !existing.sourcePrompt || !existing.subject || !existing.grade;
    if (!needsBackfill) return;

    setFormData((prev) => ({
      ...prev,
      guidedReconstruction: {
        ...(prev.guidedReconstruction || {}),
        presetKey: prev.guidedReconstruction?.presetKey || preset.key,
        subject: prev.guidedReconstruction?.subject || preset.subject,
        grade: prev.guidedReconstruction?.grade || preset.grade,
        sourcePrompt: prev.guidedReconstruction?.sourcePrompt || preset.prompt
      }
    }));
  }, [
    isGuidedTask,
    formData.guidedReconstruction?.presetKey,
    formData.guidedReconstruction?.sourcePrompt,
    formData.guidedReconstruction?.subject,
    formData.guidedReconstruction?.grade
  ]);

  const organisationNames = useMemo(() => {
    const names = new Set();
    allLearners.forEach((learner) => {
      const affiliation = learner.affiliation || {};
      if (orgType === 'project' && affiliation.type === 'project' && affiliation.name) {
        names.add(affiliation.name);
      }
      if (orgType === 'school' && affiliation.school) {
        names.add(affiliation.school);
      }
      if (orgType === 'district' && affiliation.district) {
        names.add(affiliation.district);
      }
    });
    return Array.from(names).sort();
  }, [allLearners, orgType]);

  const organisationLearners = useMemo(() => {
    if (!orgName) return [];
    return allLearners.filter((learner) => {
      const affiliation = learner.affiliation || {};
      if (orgType === 'project') {
        return affiliation.type === 'project' && affiliation.name === orgName;
      }
      if (orgType === 'school') {
        return affiliation.school === orgName;
      }
      if (orgType === 'district') {
        return affiliation.district === orgName;
      }
      return false;
    });
  }, [allLearners, orgType, orgName]);

  const myLearners = useMemo(() => {
    if (!user?.uid) return [];
    return allLearners.filter((learner) => {
      const managerIds = learner.managerIds || [];
      const affiliation = learner.affiliation || {};
      return managerIds.includes(user.uid)
        || affiliation.educatorId === user.uid
        || affiliation.tutorId === user.uid;
    });
  }, [allLearners, user?.uid]);

  useEffect(() => {
    if (orgName && organisationNames.includes(orgName)) return;
    setOrgName(organisationNames[0] || '');
  }, [organisationNames, orgName]);

  const candidateAccessEmails = useMemo(() => {
    const candidates = accessMode === 'organisation'
      ? organisationLearners
      : accessMode === 'my-learners'
        ? myLearners
        : [];
    return candidates
      .map((learner) => learner.email)
      .filter((email) => !!email);
  }, [accessMode, organisationLearners, myLearners]);

  useEffect(() => {
    if (accessMode === 'email') return;
    const nextKey = accessMode === 'organisation'
      ? `org:${orgType}:${orgName}`
      : `mine:${user?.uid || 'unknown'}`;
    if (!nextKey || nextKey === accessAutoKey) return;
    setAccessAutoKey(nextKey);
    if (candidateAccessEmails.length > 0) {
      setFormData(prev => ({
        ...prev,
        assignedUsers: Array.from(new Set(candidateAccessEmails))
      }));
    } else {
      setFormData(prev => ({ ...prev, assignedUsers: [] }));
    }
  }, [accessMode, orgType, orgName, candidateAccessEmails, accessAutoKey, user?.uid]);

  // Calculate total marks
  const calculateTotalMarks = () => {
    return selectedQuestionIds.reduce((total, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return total;
      if (q.type === 'stem' && q.subQuestions) {
        return total + (q.marks || q.subQuestions.length);
      }
      return total + (q.marks || 1);
    }, 0);
  };

  // Get question count (stem questions count as multiple)
  const getQuestionCount = () => {
    return selectedQuestionIds.reduce((count, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return count;
      if (q.type === 'stem' && q.subQuestions) {
        return count + q.subQuestions.length;
      }
      return count + 1;
    }, 0);
  };

  // Get question display text
  const getQuestionText = (q) => {
    if (q.type === 'stem') return q.stemText || 'Stem Question';
    return q.questionText || q.text || 'Question';
  };

  // Get type badge
  const getTypeBadge = (q) => {
    if (q.type === 'stem') {
      return (
        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded flex items-center gap-0.5">
          <FileText size={10} />
          Stem
        </span>
      );
    }
    return null;
  };

  // Add question
  const handleAddQuestion = (id) => {
    if (!selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => [...prev, id]);
      setSections(prev => [...prev, { type: 'question', id }]);
    }
  };

  // Remove question
  const handleRemoveQuestion = (id) => {
    setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
    setSections(prev => prev.filter(s => !(s.type === 'question' && s.id === id)));
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === selectedQuestionIds.length - 1)) {
      return;
    }
    const newIds = [...selectedQuestionIds];
    const idToMove = newIds[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    newIds[index] = newIds[swapIndex];
    newIds[swapIndex] = idToMove;
    setSelectedQuestionIds(newIds);
  };

  // Add note to sections
  const handleAddNote = (noteId) => {
    if (sections.some(s => s.type === 'note' && s.id === noteId)) return;
    setSections(prev => [...prev, { type: 'note', id: noteId, minReadingTime: 30 }]);
  };

  // Remove section by index
  const handleRemoveSection = (index) => {
    const section = sections[index];
    if (section.type === 'question') {
      setSelectedQuestionIds(prev => prev.filter(id => id !== section.id));
    }
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  // Move section up/down
  const moveSection = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === sections.length - 1)) return;
    const newSections = [...sections];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[swapIndex]] = [newSections[swapIndex], newSections[index]];
    setSections(newSections);
  };

  // Update min reading time for a note section
  const updateNoteReadingTime = (index, time) => {
    setSections(prev => prev.map((s, i) => i === index ? { ...s, minReadingTime: Math.max(0, parseInt(time) || 0) } : s));
  };

  // Link/unlink incorrect notes to a question section
  const toggleIncorrectNote = (sectionIndex, noteId) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const existing = s.incorrectNoteIds || [];
      const updated = existing.includes(noteId)
        ? existing.filter(id => id !== noteId)
        : [...existing, noteId];
      return { ...s, incorrectNoteIds: updated };
    }));
  };

  // Toggle question expansion
  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedQuestionId(prev => prev === id ? null : id);
  };

  // User search
  const handleUserSearch = async (query) => {
    setUserSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    const queryText = query.toLowerCase();
    const localResults = allLearners
      .filter(u => {
        const email = (u.email || '').toLowerCase();
        const name = getDisplayName(u).toLowerCase();
        return email.includes(queryText) || name.includes(queryText);
      })
      .filter(u => !formData.assignedUsers.includes(u.email))
      .slice(0, 10);

    if (localResults.length > 0) {
      setSearchResults(localResults);
      return;
    }

    try {
      const results = await taskService.searchUsersByEmail(query);
      setSearchResults(results.filter(u => !formData.assignedUsers.includes(u.email)));
    } catch (error) {
      console.error('User search failed:', error);
    }
  };

  const addUser = (email) => {
    if (!formData.assignedUsers.includes(email)) {
      setFormData(prev => ({ ...prev, assignedUsers: [...prev.assignedUsers, email] }));
    }
    setUserSearch('');
    setSearchResults([]);
  };

  const addManualEmail = () => {
    const email = userSearch.trim();
    if (email && email.includes('@') && !formData.assignedUsers.includes(email)) {
      addUser(email);
    }
  };

  const removeUser = (email) => {
    setFormData(prev => ({ ...prev, assignedUsers: prev.assignedUsers.filter(e => e !== email) }));
  };

  const toggleAssignedEmail = (email) => {
    if (!email) return;
    setFormData(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.includes(email)
        ? prev.assignedUsers.filter(e => e !== email)
        : [...prev.assignedUsers, email]
    }));
  };

  const selectAllAccessCandidates = (emails) => {
    setFormData(prev => ({
      ...prev,
      assignedUsers: Array.from(new Set(emails))
    }));
  };

  const clearAccessCandidates = () => {
    setFormData(prev => ({ ...prev, assignedUsers: [] }));
  };

  // OG Image upload
  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const url = await taskService.uploadTaskImage(bookId, task?.id || 'new', file);
      setFormData(prev => ({ ...prev, ogImage: url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveOgImage = () => {
    setFormData(prev => ({ ...prev, ogImage: '' }));
  };

  const handleTaskPdfUpload = async ({ blob, filename }) => {
    if (!blob) {
      throw new Error('Missing generated PDF blob');
    }

    setUploadingTaskPdf(true);
    try {
      const uploadResult = await taskService.uploadTaskPdf(bookId, task?.id || null, blob, filename, {
        persistToTask: !!task?.id
      });

      const updates = {
        taskPdfUrl: uploadResult.url,
        taskPdfFilename: uploadResult.filename
      };

      if (!task?.id) {
        updates.taskPdfUploadedAt = new Date().toISOString();
      }

      setFormData(prev => ({ ...prev, ...updates }));

      if (task?.id && typeof onTaskPdfUpdated === 'function') {
        onTaskPdfUpdated(updates);
      }

      return uploadResult.url;
    } catch (error) {
      console.error('Failed to upload task PDF:', error);
      throw error;
    } finally {
      setUploadingTaskPdf(false);
    }
  };

  const handleGuidedSourcePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file.');
      return;
    }

    setUploadingGuidedPdf(true);
    try {
      const uploadResult = await taskService.uploadGuidedSourcePdf(bookId, file);
      setFormData(prev => ({
        ...prev,
        guidedReconstruction: {
          ...(prev.guidedReconstruction || {}),
          sourcePdfUrl: uploadResult.url,
          sourcePdfFilename: uploadResult.filename
        }
      }));
    } catch (error) {
      console.error('Failed to upload guided source PDF:', error);
      alert('Failed to upload source PDF');
    } finally {
      setUploadingGuidedPdf(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleGuidedPresetChange = (presetKey) => {
    const preset = GUIDED_PROMPT_PRESETS.find((item) => item.key === presetKey);
    setFormData(prev => ({
      ...prev,
      guidedReconstruction: {
        ...(prev.guidedReconstruction || {}),
        presetKey,
        subject: preset?.subject || prev.guidedReconstruction?.subject || '',
        grade: preset?.grade || prev.guidedReconstruction?.grade || '',
        sourcePrompt: preset?.prompt || prev.guidedReconstruction?.sourcePrompt || ''
      }
    }));
  };

  const handleGenerateGuidedReconstruction = async () => {
    const sourcePdfUrl = formData.guidedReconstruction?.sourcePdfUrl;
    const sourcePrompt = formData.guidedReconstruction?.sourcePrompt?.trim();
    const topicFocus = formData.guidedReconstruction?.topicFocus?.trim();
    const specificProblem = formData.guidedReconstruction?.specificProblem?.trim();

    if (!sourcePdfUrl) {
      alert('Upload a source PDF first.');
      return;
    }
    if (!sourcePrompt) {
      alert('Select a subject/grade prompt preset first.');
      return;
    }
    if (!topicFocus && !specificProblem) {
      alert('Specify a topic focus or a specific source problem before generating.');
      return;
    }

    const effectivePrompt = [
      sourcePrompt,
      selectedGuidedPreset?.referenceExample
        ? `Reference style example:\n${selectedGuidedPreset.referenceExample}`
        : null,
      topicFocus ? `Topic focus: ${topicFocus}` : null,
      specificProblem ? `Specific source problem to discuss:\n${specificProblem}` : null
    ].filter(Boolean).join('\n\n');

    setGeneratingGuided(true);
    try {
      const response = await taskService.generateGuidedReconstruction({
        bookId,
        pdfUrl: sourcePdfUrl,
        prompt: effectivePrompt,
        topicFocus: topicFocus || '',
        specificProblem: specificProblem || '',
        subject: formData.guidedReconstruction?.subject || selectedGuidedPreset?.subject || '',
        grade: formData.guidedReconstruction?.grade || selectedGuidedPreset?.grade || '',
        presetKey: formData.guidedReconstruction?.presetKey || '',
        maxSteps: 6
      });

      const generated = response?.guidedReconstruction;
      if (!generated?.steps?.length) {
        throw new Error('No guided steps were generated');
      }

      setFormData(prev => ({
        ...prev,
        description: prev.description || generated.summary || prev.description,
        guidedReconstruction: {
          ...(prev.guidedReconstruction || {}),
          ...generated,
          sourcePdfUrl,
          sourcePdfFilename: prev.guidedReconstruction?.sourcePdfFilename || '',
          sourcePrompt,
          topicFocus: prev.guidedReconstruction?.topicFocus || '',
          specificProblem: prev.guidedReconstruction?.specificProblem || ''
        }
      }));
    } catch (error) {
      console.error('Failed to generate guided reconstruction:', error);
      alert(error?.message || 'Failed to generate guided reconstruction');
    } finally {
      setGeneratingGuided(false);
    }
  };

  // Save task
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a task name');
      return;
    }
    if (isGuidedTask) {
      if (!formData.guidedReconstruction?.sourcePdfUrl) {
        alert('Upload a source PDF for guided reconstruction.');
        return;
      }
      if (!formData.guidedReconstruction?.sourcePrompt?.trim()) {
        alert('Select a subject/grade prompt preset for guided reconstruction.');
        return;
      }
      const topicFocus = formData.guidedReconstruction?.topicFocus?.trim();
      const specificProblem = formData.guidedReconstruction?.specificProblem?.trim();
      if (!topicFocus && !specificProblem) {
        alert('Provide a topic focus or specific source problem for guided reconstruction.');
        return;
      }
      if ((formData.guidedReconstruction?.steps || []).length === 0) {
        alert('Generate guided steps before saving.');
        return;
      }
    } else {
      const hasContent = sections.length > 0 || selectedQuestionIds.length > 0;
      if (!hasContent) {
        alert('Please add at least one question or note');
        return;
      }
    }
    if (formData.isTimeBound && (!formData.startDate || !formData.endDate)) {
      alert('Please set both start and end dates');
      return;
    }

    // Derive questionIds from sections for backward compat
    const derivedQuestionIds = isGuidedTask
      ? []
      : (sections.length > 0
        ? sections.filter(s => s.type === 'question').map(s => s.id)
        : selectedQuestionIds);
    const sectionsToSave = isGuidedTask ? [] : sections;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        questionIds: derivedQuestionIds,
        sections: sectionsToSave,
        totalMarks: isGuidedTask ? 0 : calculateTotalMarks(),
        questionCount: isGuidedTask ? guidedStepsCount : getQuestionCount(),
        ogTitle: formData.ogTitle || formData.name,
        ogDescription: formData.ogDescription || formData.description,
        ogImage: formData.ogImage
      });
    } catch (error) {
      console.error('Failed to save task:', error);
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // Render expanded question content
  const renderQuestionContent = (q) => {
    if (q.type === 'stem') {
      return (
        <div className="mt-3 pl-4 border-l-2 border-gray-300 space-y-2">
          {q.stemImage && (
            <img src={q.stemImage} alt="Stem" className="max-h-32 rounded" />
          )}
          <div className="text-sm text-gray-600">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.stemText}</ReactMarkdown>
          </div>
          {q.subQuestions?.map((sub, idx) => (
            <div key={sub.id || idx} className="p-2 bg-gray-50 rounded text-sm">
              <span className="font-medium text-gray-700">{idx + 1}.</span>{' '}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                {sub.text}
              </ReactMarkdown>
              <div className="flex flex-wrap gap-1 mt-1">
                {sub.options?.map((opt, optIdx) => (
                  <span
                    key={opt.id || optIdx}
                    className={`px-2 py-0.5 text-xs rounded ${sub.correctOptionIds?.includes(opt.id)
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                      }`}
                  >
                    {opt.text}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Simple question
    return (
      <div className="mt-3 pl-4 border-l-2 border-gray-300">
        {q.stemImage && (
          <img src={q.stemImage} alt="Question" className="max-h-32 rounded mb-2" />
        )}
        <div className="text-sm text-gray-600 mb-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.questionText || q.text}</ReactMarkdown>
        </div>
        <div className="flex flex-wrap gap-1">
          {q.options?.map((opt, idx) => (
            <span
              key={opt.id || idx}
              className={`px-2 py-0.5 text-xs rounded ${q.correctOptionIds?.includes(opt.id)
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600'
                }`}
            >
              {opt.text}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const activeProcessingPage = processingPages.find(page => page.id === activeProcessingPageId);

  const handleProcessingContentChange = (value) => {
    setProcessingPages(prev =>
      prev.map(page => page.id === activeProcessingPageId ? { ...page, content: value } : page)
    );
  };

  const addProcessingPage = () => {
    const nextIndex = processingPages.length + 1;
    const newPage = { id: `page-${Date.now()}-${nextIndex}`, content: '' };
    setProcessingPages(prev => [...prev, newPage]);
    setActiveProcessingPageId(newPage.id);
  };

  const handleProcessNotebook = async () => {
    const allContent = processingPages.map(p => p.content).filter(Boolean).join('\n\n');
    if (!allContent.trim()) {
      alert('Please enter some content to process');
      return;
    }

    setIsProcessing(true);
    try {
      const results = await leitnerService.processLeitnerNotebook(bookId, allContent, processingOptions);
      setProcessingResults(results);
    } catch (error) {
      console.error('Failed to process notebook:', error);
      alert('Failed to process content with AI. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddAIGeneratedItem = async (item) => {
    try {
      if (item.type === 'question') {
        const questionData = {
          text: item.text,
          type: item.responseType || 'multiselect',
          options: item.options || [],
          correctOptionIds: item.correctOptionIds || [],
          explanation: item.explanation || '',
          difficulty: item.difficulty || 'medium',
          topics: item.topics || []
        };
        const newQuestion = await leitnerService.addQuestion(bookId, questionData, null, user?.uid);
        handleAddQuestion(newQuestion.id);
      }
    } catch (error) {
      console.error('Failed to add AI generated item:', error);
      alert('Failed to add item to task');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
          Back to Tasks
        </button>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            {[
              { id: 'creator', label: 'Creator View' },
              { id: 'processor', label: 'Task Processor' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${viewMode === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {viewMode === 'creator' && (
            <>
              <button
                onClick={() => setShowPdfExport(true)}
                disabled={selectedQuestionIds.length === 0 || uploadingTaskPdf}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {uploadingTaskPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                {uploadingTaskPdf ? 'Uploading PDF...' : 'Export PDF'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !canSaveTask}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : isEditing ? 'Update Task' : 'Create Task'}
              </button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'processor' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Task Processing</h2>
              <p className="text-sm text-gray-500">
                Paste text on an A4 canvas and preview it with inline math rendering.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={inlineMathEnabled}
                  onChange={(e) => setInlineMathEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                />
                Inline math enabled
              </label>
              <button
                onClick={addProcessingPage}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                Add Page
              </button>
              <button
                onClick={handleProcessNotebook}
                disabled={isProcessing || !processingPages.some(p => p.content.trim())}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {isProcessing ? 'Processing...' : 'AI Process'}
              </button>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex flex-wrap gap-4 items-center">
            <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">AI Options:</span>
            {[
              { id: 'generateQuiz', label: 'Quiz', icon: HelpCircle },
              { id: 'generateExercises', label: 'Exercises', icon: BrainCircuit }
            ].map(opt => (
              <label key={opt.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={processingOptions[opt.id]}
                  onChange={(e) => setProcessingOptions(prev => ({ ...prev, [opt.id]: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="flex items-center gap-1.5 text-sm text-gray-700 group-hover:text-indigo-600 transition">
                  <opt.icon size={14} className="text-indigo-500" />
                  {opt.label}
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {processingPages.map((page, index) => (
              <button
                key={page.id}
                onClick={() => setActiveProcessingPageId(page.id)}
                className={`px-3 py-1.5 text-sm rounded-full border transition ${activeProcessingPageId === page.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 text-gray-600 hover:text-gray-900'
                  }`}
              >
                Page {index + 1}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Paste Text</h3>
                <span className="text-xs text-gray-500">
                  Use inline math like <span className="font-mono">$x^2$</span> or <span className="font-mono">\(x^2\)</span>
                </span>
              </div>
              <div className="w-full max-w-[760px] aspect-[210/297] bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
                <div className="w-full h-full">
                  <InlineMathField
                    value={activeProcessingPage?.content || ''}
                    onChange={handleProcessingContentChange}
                    rows={20}
                    placeholder="Paste or type your maths/science text here... (Ctrl+M for math)"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Rendered Preview</h3>
                <span className="text-xs text-gray-500">A4 page preview</span>
              </div>
              <div className="w-full max-w-[760px] aspect-[210/297] bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
                <div className="w-full h-full p-6 overflow-auto">
                  <MathRenderer
                    content={activeProcessingPage?.content || ''}
                    enableMarkdown={true}
                    enableMath={inlineMathEnabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {processingResults && (
            <div className="mt-8 border-t border-gray-200 pt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-indigo-600" />
                  AI Generated Content
                </h3>
                <button
                  onClick={() => setProcessingResults(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Results
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {processingResults.questions?.map((q, idx) => (
                  <div key={`ai-q-${idx}`} className="bg-white border border-purple-100 rounded-lg p-4 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-purple-600 uppercase">Question</span>
                      <button
                        onClick={() => handleAddAIGeneratedItem({ type: 'question', ...q })}
                        className="text-indigo-600 hover:text-indigo-700 text-xs font-medium flex items-center gap-1"
                      >
                        <Plus size={12} /> Add to Task
                      </button>
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-3 font-medium">{q.text}</p>
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {q.options?.slice(0, 2).map((opt, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-50 border border-gray-100 rounded text-gray-400">
                          {opt.text}
                        </span>
                      ))}
                      {q.options?.length > 2 && <span className="text-[10px] text-gray-400">+{q.options.length - 2} more</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'creator' && (
        <>
          {/* Basic Info */}
          <div className="bg-gray-50 rounded-lg p-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., Week 1 Math Assessment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Brief description..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
              <select
                value={formData.type}
                onChange={(e) => {
                  const nextType = e.target.value;
                  if (nextType !== 'guided_reconstruction') {
                    setFormData(prev => ({ ...prev, type: nextType }));
                    return;
                  }

                  const defaultPreset = GUIDED_PROMPT_PRESETS[0];
                  setFormData(prev => ({
                    ...prev,
                    type: nextType,
                    guidedReconstruction: {
                      ...(prev.guidedReconstruction || {}),
                      presetKey: prev.guidedReconstruction?.presetKey || defaultPreset.key,
                      subject: prev.guidedReconstruction?.subject || defaultPreset.subject,
                      grade: prev.guidedReconstruction?.grade || defaultPreset.grade,
                      sourcePrompt: prev.guidedReconstruction?.sourcePrompt || defaultPreset.prompt
                    }
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="leitner_quiz">Leitner Quiz</option>
                <option value="guided_reconstruction">Guided Reconstruction</option>
              </select>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'questions', label: `Content (${isGuidedTask ? guidedStepsCount : (sections.length > 0 ? sections.length : selectedQuestionIds.length)})` },
              { id: 'access', label: 'Access' },
              { id: 'settings', label: 'Settings' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`px-4 py-2 font-medium border-b-2 transition ${activeSection === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Questions/Notes Content Section */}
          {activeSection === 'questions' && (
            <div className="space-y-4">
              {isGuidedTask ? (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-indigo-800 mb-2">Guided Reconstruction Setup</h3>
                    <p className="text-xs text-indigo-700">
                      Upload a book PDF, choose a subject/grade preset prompt, and generate guided examples plus practice questions.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Source Book PDF</label>
                      {formData.guidedReconstruction?.sourcePdfUrl ? (
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
                          <div className="font-medium truncate">{formData.guidedReconstruction?.sourcePdfFilename || 'Uploaded PDF'}</div>
                          <a
                            href={formData.guidedReconstruction?.sourcePdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:underline"
                          >
                            Open source PDF
                          </a>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No source PDF uploaded yet.</p>
                      )}
                      <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        {uploadingGuidedPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploadingGuidedPdf ? 'Uploading...' : 'Upload PDF'}
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleGuidedSourcePdfUpload}
                          disabled={uploadingGuidedPdf}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Subject + Grade Prompt Preset</label>
                      <select
                        value={formData.guidedReconstruction?.presetKey || ''}
                        onChange={(e) => handleGuidedPresetChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                      >
                        {GUIDED_PROMPT_PRESETS.map((preset) => (
                          <option key={preset.key} value={preset.key}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                          {selectedGuidedPreset?.subject || formData.guidedReconstruction?.subject || 'Subject'}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                          Grade {selectedGuidedPreset?.grade || formData.guidedReconstruction?.grade || '-'}
                        </span>
                      </div>

                      <label className="block text-xs font-semibold text-gray-600">Fixed Prompt</label>
                      <textarea
                        value={formData.guidedReconstruction?.sourcePrompt || ''}
                        rows={6}
                        readOnly={true}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      />
                      {selectedGuidedPreset?.referenceExample && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
                          <div className="text-[11px] font-semibold text-gray-600 mb-1">Reference Example</div>
                          <p className="text-xs text-gray-600 whitespace-pre-wrap">{selectedGuidedPreset.referenceExample}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Topic Focus</label>
                        <input
                          type="text"
                          value={formData.guidedReconstruction?.topicFocus || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            guidedReconstruction: {
                              ...(prev.guidedReconstruction || {}),
                              topicFocus: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="e.g., functions, trig equations, simultaneous equations"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Specific Source Problem (Alternative to Topic)</label>
                        <textarea
                          value={formData.guidedReconstruction?.specificProblem || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            guidedReconstruction: {
                              ...(prev.guidedReconstruction || {}),
                              specificProblem: e.target.value
                            }
                          }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                          placeholder="Paste a specific problem statement from the uploaded source."
                        />
                        <p className="mt-1 text-[11px] text-gray-500">Provide at least one of Topic Focus or Specific Source Problem.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateGuidedReconstruction}
                        disabled={generatingGuided || !formData.guidedReconstruction?.sourcePdfUrl || !formData.guidedReconstruction?.sourcePrompt?.trim() || (!formData.guidedReconstruction?.topicFocus?.trim() && !formData.guidedReconstruction?.specificProblem?.trim())}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {generatingGuided ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                        {generatingGuided ? 'Generating...' : 'Generate Guided Steps'}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-800">Generated Steps</h3>
                      <span className="text-xs text-gray-500">{guidedStepsCount} step{guidedStepsCount === 1 ? '' : 's'}</span>
                    </div>
                    {guidedStepsCount === 0 ? (
                      <p className="text-sm text-gray-500">Generate steps to preview guided reconstruction content.</p>
                    ) : (
                      <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                        {(formData.guidedReconstruction?.steps || []).map((step, index) => (
                          <div key={step.id || index} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                            <div className="text-xs font-semibold text-indigo-700 uppercase">Step {index + 1}</div>
                            <div className="text-sm font-medium text-gray-900">{step.title}</div>
                            {step.objective && <p className="text-xs text-gray-600">{step.objective}</p>}
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-1">Worked Example</div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">{step.workedExample}</div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-1">Practice Question</div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">{step.practiceQuestion}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
              {/* Sub-toggle: Add Questions | Add Notes */}
              <div className="flex items-center gap-2">
                {[
                  { id: 'questions', label: 'Add Questions', icon: FileText },
                  { id: 'notes', label: 'Add Notes', icon: BookOpen }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setContentMode(id)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition flex items-center gap-2 ${contentMode === id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Category Filter (questions mode only) */}
              {contentMode === 'questions' && (
                <CategoryFilter
                  categories={categories}
                  selectedFilters={filters}
                  onFilterChange={setFilters}
                  onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
                  questions={questions}
                />
              )}

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Available Questions or Notes */}
                <div>
                  {contentMode === 'questions' ? (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center justify-between">
                        <span>Available Questions</span>
                        <span className="text-sm text-gray-500">{availableQuestions.length} questions</span>
                      </h3>
                      <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                        {availableQuestions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">
                            {questions.length === 0 ? 'No questions available.' : 'No questions match the selected filters.'}
                          </p>
                        ) : (
                          availableQuestions.map(q => (
                            <div key={q.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              <div
                                onClick={() => handleAddQuestion(q.id)}
                                className="p-3 cursor-pointer hover:bg-gray-50 transition"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-900 line-clamp-2">
                                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                                        {getQuestionText(q)}
                                      </ReactMarkdown>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                      {q.subject && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{q.subject}</span>}
                                      {q.grade && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{q.grade}</span>}
                                      {q.topics?.map(topic => (
                                        <span key={topic} className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{topic}</span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {getTypeBadge(q)}
                                    {(q.marks > 1 || q.type === 'stem') && (
                                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded flex items-center gap-0.5">
                                        <Award size={10} />
                                        {q.marks || (q.type === 'stem' ? q.subQuestions?.length : 1)}
                                      </span>
                                    )}
                                    <button
                                      onClick={(e) => toggleExpand(q.id, e)}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                    >
                                      <ChevronRight className={`h-4 w-4 transition ${expandedQuestionId === q.id ? 'rotate-90' : ''}`} />
                                    </button>
                                    <span className="text-xs text-indigo-600 font-medium">+ Add</span>
                                  </div>
                                </div>
                              </div>
                              {expandedQuestionId === q.id && (
                                <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                                  {renderQuestionContent(q)}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center justify-between">
                        <span>Available Notes</span>
                        <span className="text-sm text-gray-500">{availableNotes.length} notes</span>
                      </h3>
                      <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                        {availableNotes.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">No notes available. Create notes in the Notes Manager.</p>
                        ) : (
                          availableNotes.map(note => {
                            const alreadyAdded = sections.some(s => s.type === 'note' && s.id === note.id);
                            return (
                              <div
                                key={note.id}
                                onClick={() => !alreadyAdded && handleAddNote(note.id)}
                                className={`bg-white rounded-lg border border-gray-200 p-3 transition ${alreadyAdded ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <BookOpen className="h-4 w-4 text-teal-500 flex-shrink-0" />
                                      <span className="text-sm font-medium text-gray-900 truncate">{note.title || 'Untitled Note'}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${note.type === 'youtube' ? 'bg-red-100 text-red-600' :
                                        note.type === 'pdf' ? 'bg-blue-100 text-blue-600' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                        {note.type || 'text'}
                                      </span>
                                      {note.subject && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{note.subject}</span>}
                                      {note.grade && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{note.grade}</span>}
                                      {note.topic && <span className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{note.topic}</span>}
                                    </div>
                                  </div>
                                  {!alreadyAdded && (
                                    <span className="text-xs text-indigo-600 font-medium flex-shrink-0">+ Add</span>
                                  )}
                                  {alreadyAdded && (
                                    <span className="text-xs text-gray-400 flex-shrink-0">Added</span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Right Column: Selected Content (unified sections) */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center justify-between">
                    <span>Selected Content</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-500">
                        {sections.filter(s => s.type === 'question').length} Qs, {sections.filter(s => s.type === 'note').length} Notes
                      </span>
                      <span className="text-amber-600 flex items-center gap-1 font-medium">
                        <Award size={14} />
                        {calculateTotalMarks()} marks
                      </span>
                    </div>
                  </h3>

                  <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {sections.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">Add questions or notes to build your task.</p>
                    ) : (
                      sections.map((section, index) => {
                        if (section.type === 'question') {
                          const q = questions.find(qu => qu.id === section.id);
                          if (!q) return null;
                          return (
                            <div key={`q-${section.id}-${index}`} className="bg-white rounded-lg border border-indigo-200 overflow-hidden">
                              <div className="flex items-center gap-2 p-3">
                                <div className="flex flex-col gap-0.5">
                                  <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronUp size={14} />
                                  </button>
                                  <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronDown size={14} />
                                  </button>
                                </div>
                                <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-gray-900 line-clamp-2">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                                      {getQuestionText(q)}
                                    </ReactMarkdown>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1 mt-1">
                                    {q.type === 'stem' && (
                                      <span className="text-xs text-purple-600">Stem with {q.subQuestions?.length || 0} sub-questions</span>
                                    )}
                                    {q.topics?.slice(0, 2).map(topic => (
                                      <span key={topic} className="text-xs text-indigo-600 bg-indigo-50 px-1 rounded">{topic}</span>
                                    ))}
                                    {section.incorrectNoteIds?.length > 0 && (
                                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                        <Link className="h-3 w-3" />
                                        {section.incorrectNoteIds.length} linked note{section.incorrectNoteIds.length > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                    {q.marks || (q.type === 'stem' ? q.subQuestions?.length : 1)}pts
                                  </span>
                                  <button
                                    onClick={() => setShowNoteLinker(showNoteLinker === index ? null : index)}
                                    className={`p-1 transition ${showNoteLinker === index ? 'text-amber-500' : 'text-gray-400 hover:text-gray-600'}`}
                                    title="Link notes for incorrect answers"
                                  >
                                    <Link className="h-4 w-4" />
                                  </button>
                                  <button onClick={(e) => toggleExpand(q.id, e)} className="p-1 text-gray-400 hover:text-gray-600">
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => handleRemoveSection(index)} className="p-1 text-red-400 hover:text-red-600">
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                              {/* Note linker dropdown */}
                              {showNoteLinker === index && availableNotes.length > 0 && (
                                <div className="px-3 pb-3 border-t border-gray-100 bg-amber-50/50">
                                  <p className="text-xs text-gray-500 mb-2 mt-2">Select notes to show when answered incorrectly:</p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {availableNotes.map(note => (
                                      <label key={note.id} className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-white cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={(section.incorrectNoteIds || []).includes(note.id)}
                                          onChange={() => toggleIncorrectNote(index, note.id)}
                                          className="w-3.5 h-3.5 rounded text-amber-500"
                                        />
                                        <BookOpen className="h-3.5 w-3.5 text-teal-500 flex-shrink-0" />
                                        <span className="truncate text-gray-700">{note.title || 'Untitled'}</span>
                                        <span className="text-xs text-gray-400 ml-auto">{note.type || 'text'}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {expandedQuestionId === q.id && (
                                <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                                  {renderQuestionContent(q)}
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (section.type === 'note') {
                          const note = availableNotes.find(n => n.id === section.id);
                          return (
                            <div key={`n-${section.id}-${index}`} className="bg-white rounded-lg border border-teal-200 overflow-hidden">
                              <div className="flex items-center gap-2 p-3">
                                <div className="flex flex-col gap-0.5">
                                  <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronUp size={14} />
                                  </button>
                                  <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronDown size={14} />
                                  </button>
                                </div>
                                <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                                <div className="p-1 bg-teal-100 rounded">
                                  <BookOpen className="h-4 w-4 text-teal-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-gray-900 truncate">{note?.title || 'Note (not found)'}</div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${note?.type === 'youtube' ? 'bg-red-100 text-red-600' :
                                      note?.type === 'pdf' ? 'bg-blue-100 text-blue-600' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>{note?.type || 'text'}</span>
                                    <div className="flex items-center gap-1">
                                      <Timer className="h-3 w-3 text-gray-400" />
                                      <input
                                        type="number"
                                        min="0"
                                        value={section.minReadingTime || 0}
                                        onChange={(e) => updateNoteReadingTime(index, e.target.value)}
                                        className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="text-xs text-gray-400">sec</span>
                                    </div>
                                  </div>
                                </div>
                                <button onClick={() => handleRemoveSection(index)} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0">
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })
                    )}
                  </div>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {/* Access Section */}
          {activeSection === 'access' && (
            <div className="space-y-6 max-w-2xl">
              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Who can access this task?</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'private', icon: Lock, label: 'Private', desc: 'Invited only' },
                    { value: 'organisation', icon: Users, label: 'Organisation', desc: 'All members' },
                    { value: 'public', icon: Globe, label: 'Public', desc: 'Anyone' }
                  ].map(({ value, icon: Icon, label, desc }) => (
                    <button
                      key={value}
                      onClick={() => setFormData(prev => ({ ...prev, visibility: value }))}
                      className={`p-4 rounded-lg border-2 transition text-left ${formData.visibility === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <Icon className={`h-5 w-5 mb-2 ${formData.visibility === value ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-xs text-gray-500">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Guest Access Toggle - Only for public tasks */}
              {formData.visibility === 'public' && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!formData.requiresSignIn}
                      onChange={(e) => setFormData(prev => ({ ...prev, requiresSignIn: !e.target.checked }))}
                      className="mt-1 w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <div>
                      <span className="font-medium text-amber-400">Allow guest access (no sign-in)</span>
                      <p className="text-sm text-amber-300/70 mt-1">
                        Users can take this task without logging in. Progress will only be saved locally on their device and won't sync across devices.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* User Assignment */}
              {formData.visibility === 'private' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign Access</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'email', label: 'Email', icon: Mail },
                        { id: 'organisation', label: 'Organisation', icon: Users },
                        { id: 'my-learners', label: 'My Learners', icon: Award }
                      ].map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setAccessMode(id)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition flex items-center gap-2 ${accessMode === id
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {accessMode === 'email' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assign Users by Email</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="email"
                            value={userSearch}
                            onChange={(e) => handleUserSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addManualEmail()}
                            placeholder="Search or enter email..."
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <button onClick={addManualEmail} disabled={!userSearch.includes('@')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      {searchResults.length > 0 && (
                        <div className="mt-2 bg-white rounded-lg border max-h-32 overflow-y-auto">
                          {searchResults.map(r => (
                            <button key={r.id} onClick={() => addUser(r.email)} className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm">
                              {r.email} {r.displayName && <span className="text-gray-400">({r.displayName})</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {formData.assignedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.assignedUsers.map(email => (
                            <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-sm">
                              {email}
                              <button onClick={() => removeUser(email)} className="hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {accessMode === 'organisation' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Organisation Type</label>
                          <select
                            value={orgType}
                            onChange={(e) => setOrgType(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="project">Project</option>
                            <option value="school">School</option>
                            <option value="district">District</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Organisation</label>
                          <select
                            value={orgName}
                            onChange={(e) => setOrgName(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            {organisationNames.length === 0 ? (
                              <option value="">No organisations found</option>
                            ) : (
                              organisationNames.map(name => (
                                <option key={name} value={name}>{name}</option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => selectAllAccessCandidates(candidateAccessEmails)}
                          disabled={candidateAccessEmails.length === 0}
                          className="px-3 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={clearAccessCandidates}
                          disabled={formData.assignedUsers.length === 0}
                          className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Clear
                        </button>
                        {loadingLearners && (
                          <span className="text-xs text-gray-400">Loading learners...</span>
                        )}
                      </div>

                      {organisationLearners.length === 0 ? (
                        <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                          No learners found for this organisation.
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {organisationLearners.map((learner) => (
                            <label key={learner.id} className="flex items-center gap-3 p-2 bg-white border rounded-lg">
                              <input
                                type="checkbox"
                                checked={!!learner.email && formData.assignedUsers.includes(learner.email)}
                                onChange={() => toggleAssignedEmail(learner.email)}
                                disabled={!learner.email}
                                className="w-4 h-4 rounded text-indigo-600 disabled:opacity-50"
                              />
                              <div className="flex-1">
                                <p className="text-sm text-gray-900">{getDisplayName(learner)}</p>
                                <p className="text-xs text-gray-500">
                                  {learner.email || 'No email'} · {getAffiliationValue(learner, 'school') || 'No school'}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {accessMode === 'my-learners' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => selectAllAccessCandidates(candidateAccessEmails)}
                          disabled={candidateAccessEmails.length === 0}
                          className="px-3 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={clearAccessCandidates}
                          disabled={formData.assignedUsers.length === 0}
                          className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Clear
                        </button>
                        {loadingLearners && (
                          <span className="text-xs text-gray-400">Loading learners...</span>
                        )}
                      </div>

                      {myLearners.length === 0 ? (
                        <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                          No learners found that you manage.
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {myLearners.map((learner) => (
                            <label key={learner.id} className="flex items-center gap-3 p-2 bg-white border rounded-lg">
                              <input
                                type="checkbox"
                                checked={!!learner.email && formData.assignedUsers.includes(learner.email)}
                                onChange={() => toggleAssignedEmail(learner.email)}
                                disabled={!learner.email}
                                className="w-4 h-4 rounded text-indigo-600 disabled:opacity-50"
                              />
                              <div className="flex-1">
                                <p className="text-sm text-gray-900">{getDisplayName(learner)}</p>
                                <p className="text-xs text-gray-500">
                                  {learner.email || 'No email'} · {getAffiliationValue(learner, 'school') || 'No school'}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Time Bound */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.isTimeBound} onChange={(e) => setFormData(prev => ({ ...prev, isTimeBound: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
                  <div>
                    <span className="font-medium text-gray-900">Time-bound Task</span>
                    <p className="text-sm text-gray-500">Set availability window</p>
                  </div>
                </label>
                {formData.isTimeBound && (
                  <div className="grid grid-cols-2 gap-4 mt-3 pl-8">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Start</label>
                      <input type="datetime-local" value={formData.startDate} onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">End</label>
                      <input type="datetime-local" value={formData.endDate} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div className="space-y-6 max-w-2xl">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Trophy className="h-4 w-4 inline mr-1" />Passing Score (%)</label>
                  <input type="number" min="0" max="100" value={formData.passingScore} onChange={(e) => setFormData(prev => ({ ...prev, passingScore: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Timer className="h-4 w-4 inline mr-1" />Time/Question (sec)</label>
                  <input type="number" min="0" value={formData.timeLimitPerQuestion} onChange={(e) => setFormData(prev => ({ ...prev, timeLimitPerQuestion: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" placeholder="0 = No limit" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Attempts</label>
                  <input type="number" min="0" value={formData.maxAttempts} onChange={(e) => setFormData(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" placeholder="0 = Unlimited" />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.shuffleQuestions} onChange={(e) => setFormData(prev => ({ ...prev, shuffleQuestions: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
                  <span className="text-gray-900"><Shuffle className="h-4 w-4 inline mr-2 text-gray-400" />Shuffle Questions</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.showAnswersAfterEachResponse}
                    onChange={(e) => setFormData(prev => ({ ...prev, showAnswersAfterEachResponse: e.target.checked }))}
                    className="w-5 h-5 rounded text-indigo-600"
                  />
                  <span className="text-gray-900"><Eye className="h-4 w-4 inline mr-2 text-gray-400" />Show Answers After Each Response (includes reasoning)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.showAnswersAfter} onChange={(e) => setFormData(prev => ({ ...prev, showAnswersAfter: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
                  <span className="text-gray-900"><Eye className="h-4 w-4 inline mr-2 text-gray-400" />Show Answers After Completion</span>
                </label>
              </div>

              {/* coLearner Settings */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-3">coLearner</h4>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.coLearnerEnabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, coLearnerEnabled: e.target.checked }))}
                    className="w-5 h-5 rounded text-indigo-600"
                  />
                  <span className="text-gray-900">Enable coLearner for this task</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={formData.coLearnerCrossTask}
                    onChange={(e) => setFormData(prev => ({ ...prev, coLearnerCrossTask: e.target.checked }))}
                    disabled={!formData.coLearnerEnabled}
                    className="w-5 h-5 rounded text-indigo-600 disabled:opacity-50"
                  />
                  <span className="text-gray-900">Allow cross-task progress context</span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  coLearner is a peer-style partner. It works with the learner, not as a teacher or tutor.
                </p>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">coLearner Prompt (Optional)</label>
                  <textarea
                    value={formData.coLearnerPrompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, coLearnerPrompt: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Steer coLearner's tone or focus. Example: Keep it calm and brief. Emphasize perseverance and small wins."
                    disabled={!formData.coLearnerEnabled}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This prompt is appended to the coLearner system guidance for this task.
                  </p>
                </div>
              </div>

              {/* Display Mode */}
              <div className="pt-4 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Layers className="h-4 w-4 inline mr-2 text-gray-400" />
                  Question Display Mode
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'one-at-a-time', label: 'One at a Time', icon: ListOrdered, desc: 'Show questions sequentially' },
                    { value: 'presentation', label: 'Presentation', icon: Columns, desc: 'Side-by-side slide view' },
                    { value: 'all-at-once', label: 'All at Once', icon: Layers, desc: 'Show all questions on one page' }
                  ].map(({ value, label, icon: Icon, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, displayMode: value }))}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${formData.displayMode === value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <Icon className={`h-5 w-5 mb-1 ${formData.displayMode === value ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <div className="font-medium text-gray-900 text-sm">{label}</div>
                      <div className="text-xs text-gray-500">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* OG Meta */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Image className="h-4 w-4 text-gray-400" />
                  Share Preview (OG Meta)
                </h4>

                {/* OG Image Upload */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-2">Share Image</label>
                  {formData.ogImage ? (
                    <div className="relative w-full max-w-md">
                      <img
                        src={formData.ogImage}
                        alt="OG Preview"
                        className="w-full h-40 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        onClick={handleRemoveOgImage}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full max-w-md h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleOgImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? (
                        <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload share image</span>
                          <span className="text-xs text-gray-400 mt-1">Recommended: 1200x630px</span>
                        </>
                      )}
                    </label>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Share Title</label>
                    <input type="text" value={formData.ogTitle} onChange={(e) => setFormData(prev => ({ ...prev, ogTitle: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder={formData.name || 'Task title'} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Share Description</label>
                    <input type="text" value={formData.ogDescription} onChange={(e) => setFormData(prev => ({ ...prev, ogDescription: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder={formData.description || 'Description'} />
                  </div>
                </div>

                {/* Preview Card */}
                {(formData.ogImage || formData.ogTitle || formData.ogDescription) && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Preview</p>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-sm">
                      {formData.ogImage && (
                        <img src={formData.ogImage} alt="Preview" className="w-full h-32 object-cover" />
                      )}
                      <div className="p-3">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {formData.ogTitle || formData.name || 'Task Title'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {formData.ogDescription || formData.description || 'Task description'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <PdfExportModal
        isOpen={showPdfExport}
        onClose={() => setShowPdfExport(false)}
        questions={selectedQuestions}
        title={formData.name || 'Task PDF'}
        preferredOrderIds={selectedQuestionIds}
        onUploadPdf={handleTaskPdfUpload}
      />
    </div>
  );
};

export default TaskCreatorManager;
